import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import * as QueryString from 'query-string';
import { FINANCIAL_SECTION_GENERAL_LINK, SIDEBAR_MENU_ITEMS, SIDEBAR_MENU_ITEMS_MANAGER, SIDEBAR_MENU_ITEMS_CUSTOMER, SIDEBAR_MENU_ITEMS_CONSULTANT, SHOULD_SHOW_ADD_CLAIM_BUTTON_IN_CLAIM_GROUP_SIDEBAR, SHOW_CLAIM_GROUPS_SELECTOR } from '@rd-web-markets/market/dist/constants';
import SidebarPortal from '../../util/SidebarPortal';
import getClaimGroupsService from '../../services/claim_groups/getClaimGroups.service';
import ClaimGroupSelector from '../../claim_group/ClaimGroupSelector';
import { useTranslation } from 'react-i18next';
import ClaimGroupSidebarV2 from './ClaimGroupSidebarV2';
import { USE_V2_DESIGN } from '@rd-web-markets/market/dist/constants';
const ClaimGroupSidebar = _ref => {
  let {
    claimGroup,
    highlightedMenuItem,
    showMenuItems = true,
    company,
    claims,
    accountType,
    customMenuItems = []
  } = _ref;
  const {
    t
  } = useTranslation();
  const [loading, setLoading] = useState(false);
  const {
    claim_group_step: claimGroupStep
  } = useParams();
  const user = useSelector(state => state.auth.user);
  const [claimGroups] = getClaimGroupsService.useFetchAllClaimGroups(company.id, setLoading);
  const {
    search
  } = useLocation();
  const isV2 = USE_V2_DESIGN;
  const userClaimGroupAbilities = window.MARKET_SETUP.useGetUserClaimGroupAbilities({
    user,
    claimGroup
  });
  const userClaimGroupAbilitiesWithReview = window.MARKET_SETUP.useGetUserClaimGroupAbilities({
    user,
    claimGroup,
    reviewedObject: claimGroup
  });
  const pagesAccess = userClaimGroupAbilitiesWithReview?.pages;
  const canViewReport = pagesAccess.MyClaimGroupsPage.components.ClaimGroupList.canAccessViewReport;
  const getLink = menuItem => {
    if (menuItem.link === 'edit/general') {
      return `claim_groups/${claimGroup.id}/${menuItem.link}`;
    } else if (menuItem.link === 'technical_section') {
      return `claim_groups/${claimGroup.id}/technical_proof/project_reports`;
    } else if (menuItem.link === 'project_overview') {
      return `claim_groups/${claimGroup.id}/project_overview`;
    } else if (menuItem.link === 'workflow') {
      return `claim_groups/${claimGroup.id}/workflow`;
    } else if (menuItem.link === 'organisational_chart') {
      return `claim_groups/${claimGroup.id}/organisational_chart`;
    } else if (menuItem.link === 'project_description') {
      return `claim_groups/${claimGroup.id}/project_overview`;
    } else if (menuItem.link === 'templates') {
      return `claim_groups/${claimGroup.id}/templates`;
    } else if (menuItem.link === 'dashboards' && user.account_type === 'customer') {
      const claimGroupYear = claimGroup.name?.slice(-4);
      return `dashboard/${claimGroup.company_id}?selected_year=${claimGroupYear}`;
    } else if (menuItem.link === 'dashboard') {
      return company.dashboard_type === 'business_unit_intensity_breakdown' ? `claim_groups/${claimGroup.id}/regular_realtime_dashboard/${company.id}` : `claim_groups/${claimGroup.id}/dashboard/${company.id}`;
    } else if (menuItem.link === 'data_centre') {
      return `claim_groups/${claimGroup.id}/${menuItem.link}/main_page`;
    } else if (menuItem.link === 'technical_narratives') {
      return `claim_groups/${claimGroup.id}/${menuItem.link}`;
    } else if (menuItem.link === 'claim_group_dashboard') {
      return `claim_groups/${claimGroup.id}/${menuItem.link}`;
    } else if (menuItem.link === 'technical_uploads') {
      return `claim_groups/${claimGroup.id}/${menuItem.link}`;
    } else if (menuItem.link === 'de/edit_company_info') {
      return `companies/${company.id}/edit`;
    } else if (menuItem.link === 'de/element_overview') {
      return `companies/${company.id}/master`;
    } else if (menuItem.hasLinkReady) {
      return menuItem.link;
    } else {
      return `companies/${claimGroup.company_id}/${claimGroup.id}/${menuItem.link}`;
    }
  };
  const getSubMenuItems = menu => {
    if (menu.link === 'claim_costs') {
      const subMenuItems = [];
      claims.forEach(claim => {
        if (SHOW_CLAIM_GROUPS_SELECTOR && claim.claim_group_id === claimGroup.id) {
          subMenuItems.push({
            text: claim.label,
            link: `claims/${claim.id}/financial_details/${FINANCIAL_SECTION_GENERAL_LINK}`
          });
        } else if (!SHOW_CLAIM_GROUPS_SELECTOR) {
          subMenuItems.push({
            text: menu.showLastFourChars ? claim.name.slice(-4) : claim.name,
            link: `claims/${claim.id}/financial_details/${FINANCIAL_SECTION_GENERAL_LINK}`
          });
        }
      });
      if (SHOULD_SHOW_ADD_CLAIM_BUTTON_IN_CLAIM_GROUP_SIDEBAR) {
        subMenuItems.push({
          text: 'Add Claim',
          link: `add_claim/company/${company.id_in_provider_system}/claim_groups/${claimGroup.id}`,
          condition: _ref2 => {
            let {
              userAbilities
            } = _ref2;
            return userAbilities.pages.any.canSeeAddClaimSidebarButton;
          }
        });
      }
      if (menu.showWages) {
        subMenuItems.unshift({
          text: 'All Base',
          link: `companies/${claimGroup.company_id}/${claimGroup.id}/base/wages`
        }, {
          text: 'All Claims',
          link: `companies/${claimGroup.company_id}/${claimGroup.id}/claims/wages`
        }, {
          text: 'All Claims and Base',
          link: `companies/${claimGroup.company_id}/${claimGroup.id}/all/wages`
        });
      }
      return subMenuItems;
    } else {
      return menu.subMenu.map(subMenuItem => {
        return {
          text: subMenuItem.text,
          link: getLink(subMenuItem),
          highlighted: subMenuItem.link === highlightedMenuItem || subMenuItem.highlighted,
          condition: subMenuItem.condition
        };
      }).filter(Boolean);
    }
  };
  const getCustomerSideBarItems = items => {
    const ukCustomerDashboardAccess = user.user_accesses.find(uA => uA.rollable_id === claimGroup.id && uA.rollable_type === 'ClaimGroup')?.dashboard_access;
    if (!ukCustomerDashboardAccess) {
      return items.filter(item => item.text !== 'dashboard');
    } else {
      return items;
    }
  };
  const getAccountTypeSidebarItems = accountType => {
    if (accountType === 'customer') {
      return getCustomerSideBarItems(SIDEBAR_MENU_ITEMS_CUSTOMER);
    } else if (accountType === 'consultant') {
      if (user && user.show_dashboard && ['/be'].includes(process.env.PUBLIC_URL)) {
        const links = [...SIDEBAR_MENU_ITEMS_CONSULTANT];
        links.push({
          text: t('dashboards'),
          link: 'claim_group_dashboard',
          icon: 'dashboard'
        });
        return links;
      } else {
        return SIDEBAR_MENU_ITEMS_CONSULTANT;
      }
    } else if (accountType === 'manager') {
      return SIDEBAR_MENU_ITEMS_MANAGER;
    } else {
      return SIDEBAR_MENU_ITEMS;
    }
  };
  let items = getAccountTypeSidebarItems(user.account_type);
  items = items.concat(customMenuItems);
  const headerItem = user.account_type === 'customer' ? {
    link: 'companies',
    text: claimGroup.name
  } : {
    link: `companies/${claimGroup.company_id}/master`,
    text: claimGroup.name
  };
  const processedMenuItems = claimGroup && items.map((item, index) => {
    let subMenuItems = item.subMenu ? getSubMenuItems(item) : false;
    if (!subMenuItems.length) {
      subMenuItems = false;
    }
    const menuItem = {
      text: item.text,
      highlighted: item.link === highlightedMenuItem || item.highlighted,
      link: getLink(item),
      icon: item.icon,
      isOpen: item.isOpen,
      subMenu: subMenuItems,
      functional: item.functional,
      // required only for check below - as a quick fix
      condition: item.condition // This can be undefined
    };
    return menuItem;
  });
  let finalMenuItems = [];
  let claimGroupSelectorAdded = false;
  processedMenuItems.forEach(item => {
    if (item.condition && !item.condition({
      userAbilities: userClaimGroupAbilities
    })) {
      // skip this item if condition exists, but does not pass
      return;
    }
    if (item.subMenu?.length) {
      // filter submenu items either by them not having any condition, 
      // or if they do have condition - check that it passes
      item.subMenu = item.subMenu.filter(item => !item.condition || item.condition({
        userAbilities: userClaimGroupAbilities
      }));
    }
    finalMenuItems.push(item);
    if (SHOW_CLAIM_GROUPS_SELECTOR && item.text === 'my_company_info' || item.functional == 'claim_group_selector' && !claimGroupSelectorAdded) {
      finalMenuItems.push({
        isClaimGroupSelector: true,
        text: 'claim_group_selector',
        highlighted: false
      });
      claimGroupSelectorAdded = true;
    }
  });
  finalMenuItems = finalMenuItems.filter(item => !item.functional);
  const claimCostItem = [{
    text: `Company Cost ${claimGroup.name.slice(-5)}`,
    highlighted: true,
    link: `companies/${claimGroup.company_id}/${claimGroup.id}/claim_costs`,
    subMenu: false
  }];
  const renderCustomMenu = items => {
    return items.map((item, index) => {
      if (item.isClaimGroupSelector) {
        return /*#__PURE__*/React.createElement(ClaimGroupSelector, {
          key: `claim-group-selector-${index}`,
          accountType: accountType,
          loading: loading,
          company: company,
          claimGroup: claimGroup,
          allClaimGroups: claimGroups
        });
      }
      return null;
    }).filter(Boolean);
  };
  if (accountType === 'customer' && canViewReport && claimGroup?.report_template_rnd?.id) {
    finalMenuItems.push({
      text: 'R&D Report',
      link: `report_template/${claimGroup.report_template_rnd.id}`,
      highlighted: highlightedMenuItem === 'rnd_report'
    });
  }
  if (isV2) {
    return /*#__PURE__*/React.createElement(ClaimGroupSidebarV2, {
      claimGroup: claimGroup,
      company: company,
      claims: claims,
      accountType: accountType,
      highlightedMenuItem: highlightedMenuItem
    });
  }
  return /*#__PURE__*/React.createElement(SidebarPortal, {
    menuItems: claimGroupStep === 'claim_costs' && !showMenuItems ? claimCostItem : finalMenuItems,
    headerItem:
    // Add this before we release new claim group master page for other markets
    !SHOW_CLAIM_GROUPS_SELECTOR ? headerItem : {
      link: '',
      text: 'home'
    },
    dividerPosition: 1,
    showMenuItems: showMenuItems,
    renderCustomItems: renderCustomMenu
  });
};
export default ClaimGroupSidebar;